<style lang="postcss">
	.hidden-file-input {
		position: absolute;
		width: 1px;
		height: 1px;
		opacity: 0;
	}
	.file-input {
		width: 100%;
	}
	.file-label {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		padding: 1.5rem 0.5rem;
		text-align: center;
		border-width: 1.25px;
		border-style: dashed;
		border-color: var(--border-neutral);
		border-radius: 0.25rem;
		gap: 0.5rem;
	}
	.file-label.disabled {
		cursor: not-allowed;
	}
	.can-hover {
		opacity: 0.4;
	}
	.file-label:not(.disabled):hover .can-hover {
		opacity: 1;
	}
	.file-label-in {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 1.25rem;
		height: 1.25rem;
	}
	.file-list {
		display: flex;
		flex-direction: column;
	}
	.file-name-cover {
		width: 100%;
		display: flex;
		flex-flow: column;
		line-height: 1.2;
	}
	.file-item {
		display: flex;
		flex-direction: row;
		align-items: stretch;
		margin-top: 1.5rem;
		border-radius: 0.25rem;
		gap: 0.5rem;
	}
	.file-info-cover {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		font-size: 0.875rem;
		flex: 1;
		min-width: 0;
	}
	.file-item-right {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: space-between;
		font-size: 0.875rem;
		line-height: 1;
		padding: 0.1rem 0;
	}
	.file-name {
		display: flex;
		flex-direction: row;
		gap: 0.5rem;
		width: 100%;
		justify-content: space-between;
	}
	.file-name-text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.file-description {
		display: flex;
		flex-flow: row;
		align-items: center;
		font-size: 0.75rem;
		opacity: 0.5;
		margin-top: 1px;
	}
	.file-progress-bar-background {
		width: 100%;
		margin-bottom: 2px;
		height: 0.25rem;
		border-radius: 0.25rem;
	}
	.file-progress-bar {
		width: 100%;
		height: 100%;
		border-radius: 0.25rem;
	}
	.thumbnail {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3.2rem;
		min-width: 3.2rem;
		height: 3.2rem;
		border-radius: 4px;
		object-fit: cover;
	}
	.thumbnail:not(.type-image) {
		border-width: 1px;
		border-style: solid;
	}
	.thumbnail > img {
		object-fit: cover;
	}
	.remove-file {
		background: rgb(220 38 38 / 1);
		color: white;
		opacity: 0.75;
		border-radius: 3px;
		width: 1rem;
		height: 1rem;
		text-align: center;
		font-size: 10px;
		font-weight: 600;
	}
	.remove-file:hover {
		opacity: 1;
	}
</style>

<script lang="ts">
	import { formatBytes, sanitizeFilename } from "$lib/helpers/functions";
	import { slide } from "$lib/transitions/slide";
	import { BROWSER } from "esm-env";
	import { createEventDispatcher, getContext, onMount } from "svelte";
	import { getFileTypes } from "$lib/data/files";
	import { v4 as uuid } from "@lukeed/uuid";

	import type { t as tBase } from "$lib/helpers/translate";
	const t = getContext<typeof tBase>("t");

	import type tus from "tus-js-client";
	export let placeholder: string = "";
	export let name: string;
	export let element: HTMLInputElement = {} as HTMLInputElement;
	export let value: FileUploadResponseField["value"] = [];
	export let disabled = false;
	export let max = null;
	export let maxSize = 1024 * 5;
	export let maxSizeTotal = undefined;
	export let allowedTypes: string[] = [];
	export let allowedExtensions: string = "";
	let inProgress = 0;

	interface PreviousUpload {
		size: number | null;
		metadata: object;
		creationTime: string;
		urlStorageKey: string;
	}

	$: {
		inProgress;
		dispatch("progressChange", {
			value: inProgress
		});
	}

	interface FileUploadDisplayPlus extends FileUploadDisplay {
		terminate?: any;
	}

	let tus: any;
	const fileTypes = getFileTypes();
	const dispatch = createEventDispatcher();
	const endpoint = `${
		import.meta.env.PUBLIC_SUPABASE_URL
	}/storage/v1/upload/resumable`;

	let loading = false;
	let invalid: string | false = false;
	let isDragOver = false;
	let fileList: FileUploadDisplayPlus[] = [];
	let debug = false;

	function createAcceptString(types: string[], extensions: string) {
		let acceptString = "";
		for (const type of types) {
			if (type === "custom") {
				acceptString +=
					extensions
						.split(/[,;:\s]+/)
						.map((ext) => `.${ext.toLocaleLowerCase()}`)
						.join(",") + ",";
			} else {
				const find = fileTypes.find((f) => f.value === type);
				acceptString += `${find?.mimes},`;
			}
		}
		return acceptString.slice(0, -1) || "*/*";
	}

	$: accept = createAcceptString(allowedTypes ?? [], allowedExtensions);

	const getFileInfo = async (keys: string[]) => {
		if (keys.length === 0) return [];
		try {
			const fetchObject = await fetch(
				`/public/form/get-file?keys=${encodeURIComponent(keys.join(";"))}`,
				{
					method: "GET",
					headers: {
						"fly-prefer-region": import.meta.env.PUBLIC_FLY_PRIMARY_REGION
					}
				}
			);

			if (!fetchObject.ok) {
				return [];
			}

			const result = await fetchObject.json();
			return result;
		} catch (error) {
			return [];
		}
	};

	const updateValue = async (fileList: FileUploadDisplay[]) => {
		let files = fileList.filter((f) => f.status === "success");
		value = files.map(({ id, key, name, size }) => ({ id, key, name, size }));
	};

	function sanitizeExtensions(input: string): string[] {
		// Split the string at commas, colons, or spaces
		const extensions = input.split(/[,;:\s]+/);

		// Sanitize, dedupe, and return the extensions
		return Array.from(
			new Set( // This will ensure uniqueness
				extensions.map(
					(ext) =>
						ext
							.trim() // Trim spaces
							.toLowerCase() // Convert to lowercase
							.replace(/^\./, "") // Remove leading dot, if any
				)
			)
		);
	}

	function generateUniqueFilename(filename: string): string {
		const sanitizedName = sanitizeFilename(filename);
		let uniqueName = sanitizedName;
		let counter = 1;

		while (fileList.some((f) => f.name === uniqueName)) {
			const lastDotIndex = sanitizedName.lastIndexOf(".");
			if (lastDotIndex === -1) {
				uniqueName = `${sanitizedName} (${counter})`;
			} else {
				const nameWithoutExt = sanitizedName.substring(0, lastDotIndex);
				const extension = sanitizedName.substring(lastDotIndex);
				uniqueName = `${nameWithoutExt} (${counter})${extension}`;
			}
			counter++;
		}

		return uniqueName;
	}

	const isInvalid = (file: File): false | string => {
		if (maxSize && file.size > maxSize * 1024) {
			const readableSize = formatBytes(maxSize * 1024);
			return t?.get("max_size", readableSize) ?? "File is too large";
		}
		const existing_total_size = fileList.reduce((acc, f) => acc + f.size, 0);
		if (maxSizeTotal && existing_total_size + file.size > maxSizeTotal * 1024) {
			const readableSize = formatBytes(maxSizeTotal * 1024);
			return t?.get("max_size_total", readableSize) ?? "File is too large";
		}
		if (max && fileList.length >= max) {
			return t?.get("max_allowed", max) ?? "Too many files";
		}
		let is_valid_extension = false;
		if (allowedTypes.includes("custom") && allowedExtensions) {
			const extensions = sanitizeExtensions(allowedExtensions);
			const extension = file.name.split(".").pop().toLocaleLowerCase();
			if (extensions.includes(extension)) {
				is_valid_extension = true;
			}
		}
		if (allowedTypes.filter((x) => x !== "custom").length > 0) {
			if (
				allowedTypes
					.filter((x) => x !== "custom")
					.some((type) => file.type.startsWith(type))
			) {
				is_valid_extension = true;
			}
		}
		if (allowedTypes.length === 0) {
			is_valid_extension = true;
		}
		if (!is_valid_extension) {
			return t?.get("invalid") ?? "Invalid file type";
		}
		if (file.size === 0) {
			return t?.get("invalid") ?? "Invalid file type";
		}
		return false;
	};

	function uploadFile(file: File) {
		return new Promise((resolve) => {
			if (isInvalid(file)) {
				invalid = isInvalid(file);
				setTimeout(() => {
					invalid = false;
				}, 3000);
				resolve(void 0);
				return;
			}

			const is_existing =
				fileList.find(
					(f) => f.name === file.name && f.status === "interrupted"
				) ?? null;
			const sanitizedName = is_existing
				? is_existing.name
				: generateUniqueFilename(file.name);
			const key = is_existing
				? is_existing.key
				: `temp/${uuid()}/${sanitizedName}`;
			let upload: tus.Upload;

			try {
				upload = new tus.Upload(file, {
					endpoint: endpoint,
					retryDelays: [0, 3000, 5000, 10000, 20000],
					chunkSize: 10 * 1024 * 1024,
					uploadDataDuringCreation: true,
					removeFingerprintOnSuccess: true,
					headers: {
						authorization: `Bearer ${import.meta.env.PUBLIC_SUPABASE_ANON_KEY}`,
						"x-upsert": "false"
					},
					metadata: {
						bucketName: "form",
						objectName: key,
						contentType: file.type,
						cacheControl: "0"
					},
					onError: function (error: any) {
						fileList = fileList.filter((f) => f.key !== key);
						invalid = "Invalid file";
						setTimeout(() => {
							invalid = false;
						}, 3000);
						element.value = "";
						resolve(void 0);
					},
					onProgress: function (bytesUploaded: number, bytesTotal: number) {
						let percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
						let ourFile = fileList.find((f) => f.key === key);
						if (ourFile) {
							ourFile.progress = Number(percentage);
							ourFile.status = "uploading";
							fileList = [...fileList];
						}
					},
					onSuccess: async function () {
						let ourFile = fileList.find((f) => f.key === key);
						if (ourFile) {
							ourFile.id = ourFile.key.split("/")[1];
							ourFile.progress = 100;
							ourFile.status = "success";
							fileList = [...fileList];
						}
						updateValue(fileList);
						resolve(void 0);
					}
				});
			} catch (error) {
				fileList = fileList.filter((f) => f.key !== key);
				invalid = "Invalid file";
				resolve(void 0);
			}

			if (!is_existing) {
				fileList.push({
					id: "",
					key,
					name: sanitizedName,
					size: file.size,
					type: file.type,
					status: "uploading",
					progress: 0,
					instance: upload,
					terminate: () => {
						resolve(void 0);
					}
				});
			} else {
				is_existing.instance = upload;
				is_existing.terminate = () => {
					fileList = fileList.filter((f) => f.key !== key);
					resolve(void 0);
				};
			}

			return upload.findPreviousUploads().then(function (
				previousUploads: PreviousUpload[]
			) {
				if (previousUploads.length) {
					upload.resumeFromPreviousUpload(previousUploads[0]);
				}
				upload.start();
			});
		});
	}

	const deleteFile = async (file: FileUploadDisplayPlus) => {
		// Abort upload if it's still going
		if (file.instance && typeof file.instance.abort !== "undefined") {
			if (file.terminate) {
				file.terminate();
			}
			await file.instance.abort();
		}

		// Remove file from fileList array
		fileList = fileList.filter((f) => f.key !== file.key);

		// Remove file from input
		if (element.files && element.files.length > 0) {
			const dT = new DataTransfer();
			for (let i = 0; i < element.files.length; i++) {
				if (element.files[i].name !== file.name) {
					dT.items.add(element.files[i]);
				}
			}
			element.files = dT.files;
		}

		// Delete localStorage item if it exists
		for (let i = 0; i < localStorage.length; i++) {
			const itemKey = localStorage.key(i);
			if (itemKey?.substring(0, 4) === "tus:") {
				const item: any = localStorage.getItem(itemKey);
				const parsed = JSON.parse(item);
				if (parsed.metadata.objectName === file.key) {
					localStorage.removeItem(itemKey);
				}
			}
		}

		// Delete file from server if it was fileList
		if (file.status === "success") {
			fetch("/public/form/delete-file", {
				method: "POST",
				body: JSON.stringify({ file: file.key }),
				headers: {
					"content-type": "application/json",
					"fly-prefer-region": import.meta.env.PUBLIC_FLY_PRIMARY_REGION
				}
			});
		}

		// Update value
		updateValue(fileList);
	};

	const allowFileUploads = (element: HTMLElement) => {
		if (!element) return;
		const watchForFiles = async (e: Event) => {
			const files = (e.target as HTMLInputElement).files;
			if (!files) return;
			for (let i = 0; i < files.length; i++) {
				try {
					inProgress++;
					uploadFile(files[i]).finally(() => {
						inProgress--;
					});
				} catch (error) {}
			}
		};
		element.addEventListener("change", watchForFiles);

		return {
			destroy() {
				element.removeEventListener("change", watchForFiles);
			}
		};
	};

	const getResumableUploads = () => {
		for (let i = 0; i < localStorage.length; i++) {
			const itemKey = localStorage.key(i);
			if (itemKey?.substring(0, 4) === "tus:") {
				const item: any = localStorage.getItem(itemKey);
				const parsed = JSON.parse(item);
				fileList.push({
					id: "",
					key: parsed.metadata.objectName,
					name: parsed.metadata.objectName.split("/").pop(),
					size: parsed.size,
					type: parsed.metadata.contentType,
					status: "interrupted",
					progress: 0,
					instance: {} as tus.Upload
				});
				fileList = fileList;
			}
		}
	};

	function handleDrop(event) {
		event.preventDefault();
		if (max && fileList.length >= max) return;
		const files = Array.from(event.dataTransfer.files) as File[];
		isDragOver = false;
		for (const file of files) {
			try {
				inProgress++;
				uploadFile(file).finally(() => {
					inProgress--;
				});
			} catch (error) {}
		}
	}

	const dragEnter = (event) => {
		isDragOver = true;
		event.preventDefault();
	};

	const dragLeave = (event) => {
		isDragOver = false;
		event.preventDefault();
	};

	async function loadExistingFiles() {
		if (value.length === 0) return;

		const fileInfos = await getFileInfo(value.map((x) => x.key));

		// Only show files that successfully loaded, ignore failed ones
		fileList = fileInfos.map((file) => {
			return {
				id: file.id,
				name: file.name,
				key: file.key,
				size: file.size,
				type: file.type,
				status: "success",
				progress: 100,
				instance: {} as tus.Upload
			};
		});
	}

	onMount(() => {
		if (BROWSER) {
			loadExistingFiles();
			const labelElement = document.querySelector(`label[for=${name}]`);
			labelElement.addEventListener("dragover", (event) =>
				event.preventDefault()
			);
			labelElement.addEventListener("dragenter", dragEnter);
			labelElement.addEventListener("dragleave", dragLeave);
			labelElement.addEventListener("drop", handleDrop);

			return () => {
				// Cleanup listeners when component is destroyed
				labelElement.removeEventListener("dragover", (event) =>
					event.preventDefault()
				);
				labelElement.removeEventListener("dragenter", dragEnter);
				labelElement.removeEventListener("dragleave", dragLeave);
				labelElement.removeEventListener("drop", handleDrop);
			};
		}
	});

	let FileIconSimple: any;

	onMount(async () => {
		if (BROWSER && !disabled) {
			tus = await import("tus-js-client");
			FileIconSimple = await import(
				"$form/Fields/Shared/FileIconSimple.svelte"
			);
			allowFileUploads(element);
			getResumableUploads();
		}
		if (debug) {
			fileList = [
				{
					id: "",
					key: "test",
					name: "my-file-asda-sda-sdas-da-sd-asd-asdqw-eqw-eqw-dqw-dwqd-q-d-.png",
					size: 123445,
					type: "image/png",
					status: "uploading",
					progress: 50,
					instance: {} as tus.Upload
				},
				{
					id: "",
					key: "test1",
					name: "my-file.mp3",
					size: 123445,
					type: "audio/L24",
					status: "success",
					progress: 100,
					instance: {} as tus.Upload
				},
				{
					id: "",
					key: "test9",
					name: "my-file9.zip",
					size: 123445,
					type: "application/zip",
					status: "success",
					progress: 100,
					instance: {} as tus.Upload
				},
				{
					id: "",
					key: "test2",
					name: "my-file2.zip",
					size: 123445,
					type: "video/quicktime",
					status: "success",
					progress: 100,
					instance: {} as tus.Upload
				},
				{
					id: "",
					key: "test3",
					name: "my-file3.zip",
					size: 123445,
					type: "application/pdf",
					status: "success",
					progress: 100,
					instance: {} as tus.Upload
				},
				{
					id: "",
					key: "test4",
					name: "my-file4.zip",
					size: 123445,
					type: "application/rtf",
					status: "success",
					progress: 100,
					instance: {} as tus.Upload
				}
			];
		}
	});
</script>

<div class="file-input {$$props.class || ''}">
	<input
		accept="{accept}"
		disabled="{max && fileList.length >= max}"
		class:cursor-not-allowed="{max && fileList.length >= max}"
		id="{name}"
		name="{name}"
		multiple="{true}"
		class="hidden-file-input"
		bind:this="{element}"
		type="file" />
	<label
		transition:slide
		class="file-label"
		class:disabled="{max && fileList.length >= max}"
		for="{name}">
		<span class="file-label-in">
			{#if loading}
				<div class="loader" style="width: 1rem; height: 1rem"></div>
			{:else if isDragOver}
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					width="1.25rem"
					height="1.25rem"
					viewBox="0 0 24 24"
					stroke-width="1.6"
					stroke="currentColor">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M12 4.5v15m0 0l6.75-6.75M12 19.5l-6.75-6.75"></path>
				</svg>
			{:else if invalid}
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					width="1.25rem"
					height="1.25rem"
					style="color: rgba(220 38 38 / 1)"
					viewBox="0 0 24 24"
					stroke-width="1.5"
					stroke="currentColor">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
					></path>
				</svg>
			{:else}
				<div class="can-hover">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						width="1.25rem"
						height="1.25rem"
						viewBox="0 0 24 24"
						stroke-width="1.5"
						stroke="currentColor">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
						></path>
					</svg>
				</div>
			{/if}
		</span>
		<div>
			{#if invalid}
				<div style="color: rgb(236 92 100 / 1)">
					{invalid}
				</div>
			{:else}
				<div class="h-6 can-hover">
					{@html isDragOver
						? t.get("drop_files")
						: placeholder || t.get("upload")}
				</div>
			{/if}
		</div>
	</label>
	<div class="file-list">
		{#each fileList as file (file.name)}
			<div transition:slide class="file-item">
				<div
					class="thumbnail"
					class:type-image="{file.type.startsWith('image') && file.id}">
					{#if file.status === "uploading"}
						<div class="loader"></div>
					{:else if file.type.startsWith("image") && file.id}
						<img
							alt="{file.name}"
							src="{import.meta.env
								.PUBLIC_APP_URL}/public/form/preview-file?id={file.id}" />
					{:else if FileIconSimple}
						<svelte:component
							this="{FileIconSimple.default}"
							mime="{file.type}" />
					{/if}
				</div>
				<div class="file-info-cover">
					<div class="file-name-cover">
						<div class="file-name">
							<div class="file-name-text">
								{file.name}
							</div>
							<div class="file-item-right">
								<button
									type="button"
									class="remove-file"
									on:click="{() => {
										deleteFile(file);
									}}">
									✕
								</button>
							</div>
						</div>
						<div class="file-description">
							{#if file.status === "interrupted"}
								{t.get("reupload_to_resume")}
							{:else}
								<div>
									{formatBytes(file.size)}
								</div>
							{/if}
						</div>
					</div>
					<div class="file-progress-bar-background">
						<div style="width: {file.progress}%" class="file-progress-bar">
						</div>
					</div>
				</div>
			</div>
		{/each}
	</div>
</div>
